import { Component, OnInit } from '@angular/core';
import { FeedbackService } from 'src/app/services/feedback.service';
import { MessageServiceService } from 'src/app/services/message-service.service';
import { AssignedFeedbackSession, GetFeedBackReviewDataResponse, PendingFeedbackSession } from 'src/app/models/feedback-request.model';
import { ChatMessage, FeedAgentAcceptResponse } from 'src/app/models/feedback-notification.model';

@Component({
  selector: 'app-feedback-request',
  templateUrl: './feedback-request.component.html',
  styleUrls: ['./feedback-request.component.scss']
})
export class FeedbackRequestComponent implements OnInit {

  private readonly ratingEmojiMap: { [key: number]: string } = {
    1: '😠',
    2: '☹️',
    3: '😐',
    4: '🙂',
    5: '😊'
  };

  loading = false;
  sessions: PendingFeedbackSession[] = [];
  resumingSessionId: string | null = null;

  chatActive = false;
  chatSessionId = '';
  chatPhoneNumber = '';
  chatMessages: ChatMessage[] = [];
  chatReadOnly = false;
  chatFeedbackType = 0;
  chatFeedbackId = 0;
  chatComments = '';
  chatIsPatientSatisfy: boolean | null = null;

  reviewPopupVisible = false;
  reviewLoadingId: string | null = null;
  reviewData: GetFeedBackReviewDataResponse | null = null;

  assignedPopupVisible = false;
  loadingAssigned = false;
  assignedSessions: AssignedFeedbackSession[] = [];
  acceptingFeedbackId: number | null = null;

  private fbTypeLabels: { [key: number]: string } = { 1: 'IP', 2: 'OP', 3: 'Emergency' };

  constructor(
    private feedbackService: FeedbackService,
    private messageService: MessageServiceService
  ) {}

  ngOnInit(): void {
    this.loadRequests();
  }

  loadRequests(): void {
    this.loading = true;
    const agentId = +localStorage.getItem('LOGGED_USER_CRMID') || 0;
    this.feedbackService.getPendingFeedbackRequests(agentId).subscribe(
      (res) => {
        this.loading = false;
        this.sessions = (res && res.sessions) ? res.sessions : [];
      },
      () => {
        this.loading = false;
        this.messageService.showErrorMessage('Unable to retrieve feedback requests');
      }
    );
  }

  fbTypeLabel(fbType: number): string {
    return this.fbTypeLabels[fbType] || '-';
  }

  isExited(session: PendingFeedbackSession): boolean {
    return session.isAgentExit === true;
  }

  openSession(session: PendingFeedbackSession): void {
    if (this.resumingSessionId) { return; }
    this.resumingSessionId = session.session_id;

    this.feedbackService.getFeedbackChatHistory({
      Session_Id: session.session_id,
      MobileNumber: session.mobile_number
    }).subscribe(
      (res) => {
        this.resumingSessionId = null;
        this.chatSessionId = session.session_id;
        this.chatPhoneNumber = session.mobile_number;
        this.chatMessages = (res && res.messages) ? res.messages : [];
        this.chatReadOnly = this.isExited(session);
        this.chatFeedbackType = session.fb_type;
        this.chatFeedbackId = session.feedbackId;
        this.chatActive = true;
        document.body.style.overflow = 'hidden';

        if (this.chatReadOnly && session.feedbackId) {
          this.feedbackService.displayAgentReview({ feedbackId: session.feedbackId }).subscribe(
            (reviewRes) => {
              this.chatComments = (reviewRes && reviewRes.comments) || '';
              this.chatIsPatientSatisfy = reviewRes ? reviewRes.isPatientSatisfy : null;
            },
            () => {
              this.chatComments = '';
              this.chatIsPatientSatisfy = null;
            }
          );
        }
      },
      () => {
        this.resumingSessionId = null;
        this.messageService.showErrorMessage('Unable to load chat history');
      }
    );
  }

  onChatExit(): void {
    this.chatActive = false;
    this.chatSessionId = '';
    this.chatPhoneNumber = '';
    this.chatMessages = [];
    this.chatReadOnly = false;
    this.chatFeedbackType = 0;
    this.chatFeedbackId = 0;
    this.chatComments = '';
    this.chatIsPatientSatisfy = null;
    document.body.style.overflow = 'auto';
    this.loadRequests();
  }

  viewFeedbackAnswers(session: PendingFeedbackSession): void {
    if (this.reviewLoadingId || !session.feedbackId) { return; }
    this.reviewLoadingId = session.session_id;

    this.feedbackService.getFeedBackReviewData({ feedbackId: session.feedbackId }).subscribe(
      (res) => {
        this.reviewLoadingId = null;
        this.reviewData = res;
        this.reviewPopupVisible = true;
        document.body.style.overflow = 'hidden';
      },
      () => {
        this.reviewLoadingId = null;
        this.messageService.showErrorMessage('Unable to load feedback answers');
      }
    );
  }

  closeReviewPopup(): void {
    this.reviewPopupVisible = false;
    this.reviewData = null;
    document.body.style.overflow = 'auto';
  }

  reviewQuestions(): string[] {
    return this.reviewData && this.reviewData.feedbackResponse
      ? Object.keys(this.reviewData.feedbackResponse)
      : [];
  }

  reviewAnswer(question: string): number | null {
    return this.reviewData && this.reviewData.feedbackResponse
      ? this.reviewData.feedbackResponse[question]
      : null;
  }

  reviewAnswerDisplay(question: string): string {
    const answer = this.reviewAnswer(question);
    const numericAnswer = Number(answer);

    if (!Number.isNaN(numericAnswer) && this.ratingEmojiMap[numericAnswer]) {
      return this.ratingEmojiMap[numericAnswer];
    }

    return answer == null ? '-' : String(answer);
  }

  openAssignedPopup(): void {
    this.assignedPopupVisible = true;
    document.body.style.overflow = 'hidden';
    this.loadAssignedSessions();
  }

  closeAssignedPopup(): void {
    this.assignedPopupVisible = false;
    document.body.style.overflow = 'auto';
  }

  loadAssignedSessions(): void {
    this.loadingAssigned = true;
    const agentId = +localStorage.getItem('LOGGED_USER_CRMID') || 0;
    this.feedbackService.getAssignedFeedbackRequestsPending(agentId).subscribe(
      (res) => {
        this.loadingAssigned = false;
        this.assignedSessions = (res && res.sessions) ? res.sessions : [];
      },
      () => {
        this.loadingAssigned = false;
        this.messageService.showErrorMessage('Unable to retrieve assigned feedback requests');
      }
    );
  }

  acceptAssignedFeedback(session: AssignedFeedbackSession): void {
    if (this.acceptingFeedbackId) { return; }
    this.acceptingFeedbackId = session.feedbackId;
    const agentId = +localStorage.getItem('LOGGED_USER_CRMID') || 0;

    this.feedbackService.feedAgentAccept({
      phoneNumber: session.mobileNumber,
      agentId,
      feedbackType: session.fbType,
      feedbackId: session.feedbackId
    }).subscribe(
      (response: FeedAgentAcceptResponse) => {
        this.acceptingFeedbackId = null;
        if (response && response.statusCode === 200 && response.success) {
          this.assignedSessions = this.assignedSessions.filter(s => s.feedbackId !== session.feedbackId);
          this.closeAssignedPopup();

          this.chatSessionId = response.session_id;
          this.chatPhoneNumber = session.mobileNumber;
          this.chatMessages = response.chatHistory || [];
          this.chatReadOnly = false;
          this.chatFeedbackType = session.fbType;
          this.chatFeedbackId = session.feedbackId;
          this.chatActive = true;
          document.body.style.overflow = 'hidden';

          this.loadRequests();
        } else {
          this.messageService.showErrorMessage((response && response.message) || 'Unable to accept feedback request');
        }
      },
      () => {
        this.acceptingFeedbackId = null;
        this.messageService.showErrorMessage('Unable to accept feedback request');
      }
    );
  }
}
